import rewardFeaturedCardTextureBgDefault from 'assets/images/vxl/textures/reward-featured-card-texture-bg-default.png';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useState } from 'react';
import {
  ImageBackground,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Button from 'shared/components/vxl/Button/Button';
import { Tag, TagType } from 'shared/components/vxl/Tag/Tag';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

interface FeaturedOfferCardProps extends PressableAnalyticsProps {
  id: string;
  titleText: string;
  logoImage?: ImageSourcePropType;
  backgroundImage?: ImageSourcePropType;
  onPress: () => void;
  isActivatedOffer?: boolean;
  activatedDurationText?: string;
  activateButtonLabel: string;
  onActivatePress?: () => void;
  isDisabled?: boolean;
}
export enum FeaturedOfferCardState {
  DEFAULT = 'default',
  PRESSED = 'pressed',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  HOVER = 'hover',
  FOCUSED = 'focused',
}

const defaultImage = rewardFeaturedCardTextureBgDefault as ImageSourcePropType;
const titleTextLineLimit = 2;

const FeaturedOfferCard = ({
  backgroundImage,
  logoImage,
  titleText,
  onPress,
  isActivatedOffer,
  activatedDurationText,
  activateButtonLabel,
  onActivatePress,
  isDisabled = false,
  analyticsId,
  customAttributes,
}: FeaturedOfferCardProps) => {
  const [cardState, setCardState] = useState<FeaturedOfferCardState>(() => {
    if (isDisabled) {
      return FeaturedOfferCardState.DISABLED;
    }
    return FeaturedOfferCardState.DEFAULT;
  });

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const handlePressIn = useCallback(() => {
    if (cardState !== FeaturedOfferCardState.DISABLED) {
      setCardState(FeaturedOfferCardState.PRESSED);
    }
  }, [cardState]);

  const handlePressOut = useCallback(() => {
    if (cardState !== FeaturedOfferCardState.DISABLED) {
      setCardState(FeaturedOfferCardState.DEFAULT);
    }
  }, [cardState]);

  const handleHoverIn = useCallback(() => {
    if (
      cardState !== FeaturedOfferCardState.DISABLED &&
      cardState !== FeaturedOfferCardState.PRESSED
    ) {
      setCardState(FeaturedOfferCardState.HOVER);
    }
  }, [cardState]);

  const handleHoverOut = useCallback(() => {
    if (
      cardState !== FeaturedOfferCardState.DISABLED &&
      cardState !== FeaturedOfferCardState.PRESSED
    ) {
      setCardState(FeaturedOfferCardState.DEFAULT);
    }
  }, [cardState]);

  const handleFocus = useCallback(() => {
    if (cardState !== FeaturedOfferCardState.DISABLED) {
      setCardState(FeaturedOfferCardState.FOCUSED);
    }
  }, [cardState]);

  const handleBlur = useCallback(() => {
    if (cardState !== FeaturedOfferCardState.DISABLED) {
      setCardState(FeaturedOfferCardState.DEFAULT);
    }
  }, [cardState]);

  const handleOnPress = () => {
    cardPressAnalytics();
    onPress?.();
  };

  const [imageLoadError, setImageLoadError] = useState(!backgroundImage);

  return (
    <Pressable
      style={styles.mainContainer}
      onPress={handleOnPress}
      onBlur={handleBlur}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onFocus={handleFocus}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      disabled={isDisabled}
      role="button"
      accessibilityLabel={titleText}
      testID="top-offer-card"
    >
      <View>
        <ImageBackground
          source={
            !imageLoadError && backgroundImage
              ? (backgroundImage as ImageSourcePropType)
              : defaultImage
          }
          style={styles.imageBackground}
          onError={() => setImageLoadError(true)}
        >
          {cardState === FeaturedOfferCardState.HOVER && (
            <View style={styles.hoveredStyle} />
          )}
          {cardState === FeaturedOfferCardState.PRESSED && (
            <View style={styles.pressedStyle} />
          )}
          {cardState === FeaturedOfferCardState.FOCUSED && (
            <View style={styles.focusedStyle} />
          )}
          {cardState === FeaturedOfferCardState.DISABLED && (
            <View style={styles.disabledOverlay} />
          )}
          <LinearGradient
            colors={['rgba(0, 0, 0, 0.00)', 'rgba(0, 0, 0, 0.40)']}
            locations={[0.2, 1]}
            style={styles.linearStyle}
          >
            <View style={styles.contentContainer}>
              <View style={styles.iconAvatarContainer}>
                <IconAvatar
                  variant="LG"
                  iconKey={!logoImage ? 'STORE' : undefined}
                  imageSource={logoImage}
                  defaultPlaceholderIcon="STORE"
                  iconAccessibilityLabel="store-icon"
                />
              </View>
              <View style={styles.textContainer}>
                <TitleSmText
                  style={styles.text}
                  numberOfLines={titleTextLineLimit}
                  testID="top-offer-card-title"
                >
                  {titleText}
                </TitleSmText>
                {isActivatedOffer === false && !activatedDurationText ? (
                  <Button
                    label={activateButtonLabel}
                    size="LARGE"
                    type="SECONDARY"
                    width="HUG"
                    onDark={true}
                    onPress={onActivatePress}
                  />
                ) : null}
                {isActivatedOffer && activatedDurationText ? (
                  <View style={styles.tagStyle}>
                    <Tag
                      text={activatedDurationText}
                      type={TagType.SUCCESS}
                      iconKey="CLOCK_FILLED"
                    />
                  </View>
                ) : null}
              </View>
            </View>
          </LinearGradient>
        </ImageBackground>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    justifyContent: 'space-between',
    margin: theme.spacing.m,
  },
  disabledOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.color.background.overlay,
    borderRadius: 4,
  },

  focusedStyle: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderColor: theme.color.border.focused,
    borderRadius: 4,
    borderWidth: 1,
  },
  hoveredStyle: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(28, 28, 28, 0.2)',
    borderRadius: 4,
  },
  iconAvatarContainer: {
    alignItems: 'flex-start',
    borderRadius: 4,
    height: 56,
    width: 56,
  },
  imageBackground: {
    borderRadius: 4,
    height: 200,
    overflow: 'hidden',
    width: 327,
  },
  linearStyle: {
    flex: 1,
  },
  mainContainer: {
    borderRadius: 4,
    height: 200,
    width: 327,
  },
  pressedStyle: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(28, 28, 28, 0.3)',
    borderRadius: 4,
  },
  tagStyle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    color: theme.color.text.inverse,
    flex: 1,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
  },
  textContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
  },
});

export default FeaturedOfferCard;
